import { useTranslation } from 'react-i18next';
import { TICKET_THREAD_PAGE, type TicketAuditEntry, type TicketMessagePayload } from '@nbh/shared';
import { ChatThread } from '../chat/ChatThread';
import type { ThreadMessage } from '../chat/threadModel';

/**
 * The ticket thread's body: the shared `ChatThread` in its multi-party context — sender
 * names on received bubbles, the job's lifecycle inline as system rows, and the
 * server's page cap admitted at the top when the thread fills it.
 *
 * Renders what arrives and nothing more. Internal notes are withheld from a contractor
 * at the query, not here, and the audit trail reaches this component through the same
 * detail payload the History tab reads.
 */
export function ChatMessageList({
  messages,
  events,
  viewerId,
  loading = false,
}: {
  messages: TicketMessagePayload[];
  events: TicketAuditEntry[];
  viewerId: string | undefined;
  loading?: boolean;
}) {
  const { t } = useTranslation();

  if (loading) return <p className="p-4 text-sm text-slate-400">{t('messages.loading')}</p>;

  return (
    <ChatThread
      messages={messages.map(toThreadMessage)}
      events={events}
      viewerId={viewerId}
      multiParty
      truncatedAt={TICKET_THREAD_PAGE}
      emptyText={t('chat.empty')}
    />
  );
}

function toThreadMessage(message: TicketMessagePayload): ThreadMessage {
  return {
    id: message.id,
    body: message.body,
    senderId: message.authorId,
    senderName: message.authorName,
    createdAt: message.createdAt,
    isInternal: message.isInternal,
    attachments: message.attachments,
  };
}
